/**
 * 🎯 LocationsAgGridTable Component
 *
 * Componente de tabla avanzado para ubicaciones usando AG Grid.
 * Características:
 * - Ordenamiento por columnas (code)
 * - Scroll infinito para carga de datos paginados
 * - Selección múltiple (checkboxes) e impresión masiva de etiquetas
 * - Acciones por fila (editar, eliminar, ajustar, imprimir, bloquear/desbloquear)
 * - Columnas personalizables mediante ColumnSelector
 * - Renderers personalizados para cada tipo de dato
 */

import { ColDef } from 'ag-grid-community';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AgGridTable, AgGridTableRef } from '@shared/components/ui/AgGridTable';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { Location } from '../../models/Location';
import { LocationsSelectionToolbar } from '../interaction/LocationsSelectionToolbar';

interface LocationsAgGridTableProps {
  locations: Location[];
  isLoading?: boolean;
  isFetching?: boolean; // 🎯 Añadir isFetching para control de opacidad
  className?: string;
  // 🎯 Props para ColumnSelector
  selectedColumns?: string[];
  // 🎯 Props para infinite scroll
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  // 🎯 Props para sorting personalizado
  sorting?: { field: string; direction: 'asc' | 'desc' } | null;
  onSortingChange?: (sorting: { field: string; direction: 'asc' | 'desc' } | null) => void;
  // 🎯 Props para handlers de acciones
  onLocationEdit?: (location: Location) => void;
  onLocationDelete?: (location: Location) => void;
  onLocationAdjust?: (location: Location) => void;
  onLocationPrint?: (location: Location, size: 'small' | 'large') => void;
  onLocationToggleLock?: (location: Location) => void;
  // 🎯 Selección múltiple (escritorio)
  selectedLocationIds?: Set<number>;
  onSelectionChanged?: (selectedIds: Set<number>) => void;
  onClearSelection?: () => void;
  onPrintSelectedLabels?: () => void;
  hasTeamData?: boolean;
}

export const LocationsAgGridTable = React.memo(function LocationsAgGridTable({
  locations,
  isFetching = false, // 🎯 Añadir isFetching para control de opacidad
  selectedColumns = [],
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  sorting,
  onSortingChange,
  onLocationEdit,
  onLocationDelete,
  onLocationAdjust,
  onLocationPrint,
  onLocationToggleLock,
  selectedLocationIds = new Set(),
  onSelectionChanged,
  onClearSelection,
  onPrintSelectedLabels,
  hasTeamData = false,
}: LocationsAgGridTableProps) {
  const agGridRef = useRef<AgGridTableRef>(null);
  const [localSelectedCount, setLocalSelectedCount] = useState(0);

  const clearSelectionUI = useCallback(() => {
    agGridRef.current?.deselectAll();
    setLocalSelectedCount(0);
  }, []);

  // 🎯 Evitar crash: handler no-op para onGridReady
  const handleGridReady = useCallback(() => {}, []);

  const handleSelectionChanged = useCallback(
    (selectedRows: Location[]) => {
      const selectedIds = new Set(selectedRows.map((row) => row.id));
      setLocalSelectedCount(selectedRows.length);
      onSelectionChanged?.(selectedIds);
    },
    [onSelectionChanged],
  );

  useEffect(() => {
    if (selectedLocationIds.size === 0) {
      clearSelectionUI();
    }
  }, [selectedLocationIds, clearSelectionUI]);

  // 🎯 Usar el hook para manejar las columnas (ahora con cellRenderers incluidos)
  const { filteredColumnDefs } = useAgGridColumns({
    selectedColumns,
    hasTeamData,
    onLocationEdit,
    onLocationDelete,
    onLocationAdjust,
    onLocationPrint,
    onLocationToggleLock,
  });

  // 🎯 Las columnas ya vienen con sus cellRenderers desde el hook
  // Ya no necesitamos aplicar renderers aquí, todo está en useAgGridColumns.tsx
  const columnDefsWithRenderers: ColDef[] = filteredColumnDefs;

  const handleCloseSelectionToolbar = useCallback(() => {
    clearSelectionUI();
    onClearSelection?.();
  }, [clearSelectionUI, onClearSelection]);

  const toolbarCount = localSelectedCount || selectedLocationIds.size;

  return (
    <div className={`w-full ${isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : ''}`}>
      <AgGridTable
        ref={agGridRef}
        data={locations}
        columnDefs={columnDefsWithRenderers}
        height="calc(100vh - 150px)"
        enableSelection={true}
        onSelectionChanged={handleSelectionChanged}
        onGridReady={handleGridReady}
        className="rounded-lg shadow-sm"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        gridOptions={{
          getRowId: (params) => String((params.data as Location)?.id ?? ''),
        }}
        sorting={sorting}
        onSortingChange={onSortingChange}
      />

      <LocationsSelectionToolbar
        selectedCount={toolbarCount}
        onClose={handleCloseSelectionToolbar}
        onPrintLabels={() => onPrintSelectedLabels?.()}
      />
    </div>
  );
});
